import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { Copy, FileJson, Info, ListCheck, Loader } from "lucide-react";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isAlphanumeric } from "validator";
import { getRcProDetails } from "../../../Services/ApiTestService/apiTestService";

function RcPro() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const data = response?.data?.data;

  const [vehicleNumber, setVehicleNumber] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  const handleRcPro = async () => {
    if (!profileData) return;

    const validate = isAlpha(vehicleNumber);

    if (!validate) {
      return setInputError(
        "⚠️ Please enter alpha numeric in vehicle No. field",
      );
    } else if (vehicleNumber.length < 7) {
      return setInputError("⚠️ Enter at least 7 digits in vehicle No. field");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getRcProDetails({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        vehicle_num: vehicleNumber.trim(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14">
        {/* ---------------- FORM ---------------- */}
        <div className=" max-w-3xl border rounded-md sm:p-6 mt-6 bg-white">
          <div className="flex items-center flex-col flex-wrap gap-4">
            {/* Vehicle */}
            <div className="flex items-center gap-4">
              <label className="w-32 font-bold">
                Vehicle No <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                maxLength={15}
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                className="flex-1 border border-gray-400 rounded-md px-3 h-[38px]"
                placeholder="Eg: GJ08BN1256"
              />
            </div>

            {/* show input errors */}

            {inputError && (
              <div className="text-red-800 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex justify-end ">
              <button
                disabled={isLoading || !vehicleNumber}
                onClick={handleRcPro}
                className="px-6 py-2 bg-gradient-to-br hover:bg-black active:bg-green-600 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {/* response message */}

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div className="w-full flex justify-center">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Vehicle number. Clicking the Verify button.
              After successful verification, the system will return the response
              in structured format, including contact details associated with
              the provided details. You may review and copy the data for your
              reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

           {data &&  <div className="bg-white rounded-2xl shadow-xl border overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white p-6">
                <h2 className="text-3xl font-bold flex items-center gap-3">
                  <ListCheck size={32} />
                  Full Vehicle Details
                </h2>

                <p className="opacity-90 mt-1">
                  Registration No : <b>{data.regNo}</b>
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6 p-6">
                {/* Vehicle Overview */}
                <Section
                  title="🚗 Vehicle Overview"
                  fields={[
                    ["Registration", data.regNo],
                    ["Owner", data.owner],
                    ["Status", data.status],
                    ["Registration Date", data.regDate],
                    ["RC Expiry", data.rcExpiryDate],
                    ["Owner Count", data.ownerCount],
                  ]}
                />

                {/* Vehicle Details */}
                <Section
                  title="🚘 Vehicle Details"
                  fields={[
                    ["Manufacturer", data.vehicleManufacturerName],
                    ["Model", data.model],
                    ["Fuel", data.type],
                    ["Colour", data.vehicleColour],
                    ["Vehicle Class", data.vehicleClass],
                    ["Category", data.vehicleCategory],
                    ["Seats", data.vehicleSeatCapacity],
                    ["Cylinders", data.vehicleCylindersNo],
                    ["Engine CC", data.vehicleCubicCapacity + " cc"],
                  ]}
                />

                {/* Registration */}
                <Section
                  title="📑 Registration"
                  fields={[
                    ["RTO", data.regAuthority],
                    ["RTO Code", data.rtoCode],
                    ["Norms", data.normsType],
                    ["Body Type", data.bodyType],
                    ["Manufacturing", data.vehicleManufacturingMonthYear],
                  ]}
                />

                {/* Insurance */}
                <Section
                  title="🛡 Insurance & PUC"
                  fields={[
                    ["Insurance Company", data.vehicleInsuranceCompanyName],
                    ["Insurance Valid", data.vehicleInsuranceUpto],
                    ["Policy Number", data.vehicleInsurancePolicyNumber],
                    ["PUCC Number", data.puccNumber],
                    ["PUCC Valid", data.puccUpto],
                  ]}
                />

                {/* Technical */}
                <Section
                  title="⚙ Technical"
                  fields={[
                    ["Gross Weight", data.grossVehicleWeight + " kg"],
                    ["Unladen Weight", data.unladenWeight + " kg"],
                    ["Wheelbase", data.wheelbase + " mm"],
                    ["Commercial", data.isCommercial ? "Yes" : "No"],
                    ["Financed", data.financed ? "Yes" : "No"],
                  ]}
                />

                {/* Address */}
                <Section
                  title="🏠 Address"
                  fields={[
                    ["Present", data.presentAddress],
                    ["Permanent", data.permanentAddress],
                  ]}
                />
              </div>
            </div>}
          </div>
        )}
      </div>
    </>
  );
}

export default RcPro;
const Section = ({ title, fields }) => (
  <div className="rounded-xl border bg-gray-50">
    <div className="bg-blue-50 border-b px-5 py-3">
      <h3 className="font-semibold text-blue-700 text-lg">{title}</h3>
    </div>

    <div className="divide-y">
      {fields.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-4 px-5 py-3">
          <span className="text-gray-500">{label}</span>

          <span className="font-semibold text-right break-all">
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  </div>
);
